import { type FC, forwardRef, type PropsWithChildren, type ReactNode } from "react";

import { cn } from "../../utils";
import { FieldControl, FieldDescription, FieldLabel, FieldMessage } from "./field-base-elements";
import { FormFieldContext, FormItem, useFormField } from "./form-field";
import { useField } from "./use-field";

export interface FieldBaseProps {
  name: string;
  label?: ReactNode;
  disabled?: boolean;
  readOnly?: boolean;
  labelPosition?: "start" | "end";
  labelOrientation?: "horizontal" | "vertical";
  description?: string;
  className?: string;
  suppressErrorMessage?: boolean;
  optional?: boolean;
  maxLength?: number;
}

export const FieldBase = forwardRef<HTMLDivElement, PropsWithChildren<FieldBaseProps>>(
  (
    {
      name,
      label,
      labelPosition = "start",
      labelOrientation = "vertical",
      description,
      children,
      className,
      disabled,
      optional,
      suppressErrorMessage,
      maxLength,
    },
    ref
  ) => (
    <FormFieldContext.Provider value={{ name }}>
      <FormItem
        ref={ref}
        data-disabled={disabled || undefined}
        className={cn("group/form-field space-y-0 flex flex-col flex-1 gap-1.5 min-w-0", className)}
      >
        <div
          className={cn("flex gap-1.5 flex-1", {
            "flex-col": labelOrientation === "vertical" && labelPosition === "start",
            "flex-col-reverse": labelOrientation === "vertical" && labelPosition === "end",
            "flex-row items-center": labelOrientation === "horizontal" && labelPosition === "start",
            "flex-row-reverse items-center justify-end": labelOrientation === "horizontal" && labelPosition === "end",
          })}
        >
          {label && (
            <FieldLabel className="flex gap-2 justify-between text-xs font-semibold text-muted-foreground group-data-[disabled]/form-field:cursor-not-allowed group-data-[disabled]/form-field:pointer-events-none group-data-[disabled]/form-field:opacity-70">
              <span className="flex-auto">{label}</span>
              {optional && (
                <>
                  <span className="flex-1" />
                  <span className="font-normal">Optional</span>
                </>
              )}
            </FieldLabel>
          )}

          <FieldControl>{children}</FieldControl>
        </div>

        <FieldBaseFooter
          name={name}
          description={description}
          suppressErrorMessage={suppressErrorMessage}
          maxLength={maxLength}
        />
      </FormItem>
    </FormFieldContext.Provider>
  )
);
FieldBase.displayName = "FieldBase";

interface FieldBaseFooterProps {
  name: string;
  description?: string;
  maxLength?: number;
  suppressErrorMessage?: boolean;
}

const FieldBaseFooter: FC<FieldBaseFooterProps> = ({ name, description, suppressErrorMessage, maxLength }) => {
  const { field } = useField({ name });
  const { error } = useFormField();

  const hasError = !!error && !suppressErrorMessage;

  if (!description && !hasError && !maxLength) {
    return null;
  }

  return (
    <div className="text-sm text-muted-foreground text-wrap flex gap-2 items-start min-w-0 max-w-full">
      <div className="flex flex-col gap-1 flex-auto max-w-full">
        {description && <FieldDescription>{description}</FieldDescription>}
        {!suppressErrorMessage && <FieldMessage role="alert" />}
      </div>
      <span className="flex-auto -mx-2" />
      {maxLength && (
        <p className="flex-0 text-sm text-muted-foreground text-wrap text-end">
          {field.value?.length ?? 0}/{maxLength}
        </p>
      )}
    </div>
  );
};
